/**
 * Lógica pura de lo que se adjunta a un mensaje para Sol: imágenes (hasta 5) y
 * nota de voz. El contrato con el edge `core-rag-chat` está en
 * `SendMessageRequest` (`images`, `audio`).
 */

export const MAX_IMAGENES = 5;
export const TIPOS_DE_IMAGEN = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;
/** Lado máximo tras la reducción en el navegador. */
export const LADO_MAXIMO_PX = 2000;
export const CALIDAD_JPEG = 0.85;
/** Una foto de celular pesa varios MB y se reduce; más que esto no se intenta. */
export const MAX_BYTES_IMAGEN = 20 * 1024 * 1024;
/** El GIF se manda tal cual, así que el tope es más bajo. */
export const MAX_BYTES_GIF = 8 * 1024 * 1024;

/** Tope del base64 sumado de un mensaje: el edge corta con 400 si se pasa (15 MB). */
export const MAX_BASE64_TOTAL = 15 * 1024 * 1024;

/** Aviso cuando una nota de voz no llegó a enviarse. */
export const AVISO_NOTA_FALLIDA = 'No se pudo enviar la nota de voz. Grabala de nuevo.';

export interface ImagenPreparada {
  mimeType: string;
  base64: string;
}

/** Suma del largo del base64 de las imágenes. */
export function totalBase64(imagenes: { base64: string }[]): number {
  return imagenes.reduce((acc, i) => acc + i.base64.length, 0);
}

export function esTipoDeImagenPermitido(tipo: string): boolean {
  return (TIPOS_DE_IMAGEN as readonly string[]).includes(tipo);
}

/** Motivo de rechazo en palabras, o null si el archivo sirve. */
export function motivoDeRechazo(archivo: { type: string; size: number }): string | null {
  if (!esTipoDeImagenPermitido(archivo.type)) {
    return 'Solo se pueden adjuntar imágenes JPG, PNG, WebP o GIF.';
  }
  const tope = archivo.type === 'image/gif' ? MAX_BYTES_GIF : MAX_BYTES_IMAGEN;
  if (archivo.size > tope) {
    return `La imagen pesa demasiado (máximo ${Math.round(tope / 1024 / 1024)} MB).`;
  }
  return null;
}

/** Lado a lado, sin pasarse de `LADO_MAXIMO_PX` y sin agrandar. */
export function dimensionesReducidas(ancho: number, alto: number): { ancho: number; alto: number } {
  const mayor = Math.max(ancho, alto);
  if (mayor <= LADO_MAXIMO_PX) return { ancho, alto };
  const f = LADO_MAXIMO_PX / mayor;
  return { ancho: Math.round(ancho * f), alto: Math.round(alto * f) };
}

/** Parte base64 de un `data:` URL. */
export function base64DeDataUrl(dataUrl: string): string {
  const i = dataUrl.indexOf(',');
  return i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
}

function leerComoDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error('No se pudo leer el archivo'));
    r.readAsDataURL(blob);
  });
}

/** Reduce a JPEG con canvas (GIF pasa tal cual). Corre solo en el navegador. */
export async function prepararImagen(archivo: File): Promise<ImagenPreparada> {
  if (archivo.type === 'image/gif') {
    return {
      mimeType: 'image/gif',
      base64: base64DeDataUrl(await leerComoDataUrl(archivo)),
    };
  }
  const bitmap = await createImageBitmap(archivo);
  try {
    const { ancho, alto } = dimensionesReducidas(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = ancho;
    canvas.height = alto;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Tu navegador no pudo procesar la imagen');
    // Fondo blanco: un PNG con transparencia saldría negro al pasar a JPEG.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, ancho, alto);
    ctx.drawImage(bitmap, 0, 0, ancho, alto);
    return {
      mimeType: 'image/jpeg',
      base64: base64DeDataUrl(canvas.toDataURL('image/jpeg', CALIDAD_JPEG)),
    };
  } finally {
    bitmap.close();
  }
}

/** Nota de voz lista para el edge. El tipo va sin parámetros (`;codecs=opus`). */
export async function prepararAudio(
  blob: Blob,
  duration_ms: number,
): Promise<{ mimeType: string; base64: string; duration_ms: number }> {
  return {
    mimeType: (blob.type || 'audio/webm').split(';')[0],
    base64: base64DeDataUrl(await leerComoDataUrl(blob)),
    duration_ms,
  };
}

/** Texto del indicador de espera según lo que lleva el mensaje en vuelo. */
export function textoDeCarga(adjuntos: { imagenes?: boolean; audio?: boolean } | null): string {
  if (adjuntos?.audio) return 'La IA está trabajando. Puede demorar un poco más por el audio.';
  if (adjuntos?.imagenes) {
    return 'La IA está trabajando. Puede demorar un poco más por las imágenes.';
  }
  return 'Sol está pensando…';
}

export function etiquetaDeImagenes(cantidad: number): string {
  return cantidad === 1 ? '1 imagen' : `${cantidad} imágenes`;
}

/** m:ss */
export function duracionCorta(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
